import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import {
  Plus,
  ClipboardList,
  Loader2,
  BookOpen,
  ChevronRight,
  Pencil,
  History,
  ArchiveRestore,
  Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useProgramRegisters,
  useCreateRegister,
  useDeleteRegister,
  useUpdateRegister,
  useArchivedProgramRegisters,
  useRestoreRegister,
  type ProgramRegister,
} from "@/lib/program-registration";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

// ── Create dialog ──────────────────────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
}

function CreateRegisterDialog({ open, onClose }: CreateDialogProps) {
  const { toast } = useToast();
  const createRegister = useCreateRegister();
  const [, setLocation] = useLocation();

  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [yearGroup, setYearGroup] = useState("");
  const [term, setTerm] = useState("");
  const [schoolYear, setSchoolYear] = useState("");
  const [description, setDescription] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);

  function resetForm() {
    setTitle("");
    setSubject("");
    setYearGroup("");
    setTerm("");
    setSchoolYear("");
    setDescription("");
    setTitleTouched(false);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTitleTouched(true);
    if (!title.trim()) return;

    createRegister.mutate(
      {
        title: title.trim(),
        subject: subject.trim() || null,
        yearGroup: yearGroup.trim() || null,
        term: term.trim() || null,
        schoolYear: schoolYear.trim() || null,
        description: description.trim() || null,
      },
      {
        onSuccess: (data) => {
          toast({ title: "Program register created" });
          resetForm();
          onClose();
          setLocation(`/app/program-registration/${data.register.id}`);
        },
        onError: (err: unknown) => {
          toast({
            title: "Couldn't create register",
            description: getApiErrorMessage(err, "Please try again."),
            variant: "destructive",
          });
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New program register</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="reg-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="reg-title"
              placeholder="e.g. Year 7 English — Term 2 Program"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setTitleTouched(true)}
              className={
                titleTouched && !title.trim() ? "border-destructive" : ""
              }
              autoFocus
            />
            {titleTouched && !title.trim() && (
              <p className="text-xs text-destructive">Title is required.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="reg-subject">Subject / Program name</Label>
              <Input
                id="reg-subject"
                placeholder="e.g. English"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <p className="text-xs text-gray-400">Free text — doesn't need to match a saved Subject.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-year-group">Year group</Label>
              <Input
                id="reg-year-group"
                placeholder="e.g. Year 7"
                value={yearGroup}
                onChange={(e) => setYearGroup(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-term">Term</Label>
              <Input
                id="reg-term"
                placeholder="e.g. Term 2"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-school-year">School year</Label>
              <Input
                id="reg-school-year"
                placeholder="e.g. 2025"
                value={schoolYear}
                onChange={(e) => setSchoolYear(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reg-description">Description</Label>
            <Textarea
              id="reg-description"
              placeholder="Optional overview or notes"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createRegister.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createRegister.isPending}>
              {createRegister.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create register
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit dialog ───────────────────────────────────────────────────────────────

interface EditDialogProps {
  register: ProgramRegister;
  open: boolean;
  onClose: () => void;
}

function EditRegisterDialog({ register, open, onClose }: EditDialogProps) {
  const { toast } = useToast();
  const updateRegister = useUpdateRegister(register.id);

  const [title, setTitle] = useState(register.title);
  const [subject, setSubject] = useState(register.subject ?? "");
  const [yearGroup, setYearGroup] = useState(register.yearGroup ?? "");
  const [term, setTerm] = useState(register.term ?? "");
  const [schoolYear, setSchoolYear] = useState(register.schoolYear ?? "");
  const [description, setDescription] = useState(register.description ?? "");
  const [titleTouched, setTitleTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(register.title);
      setSubject(register.subject ?? "");
      setYearGroup(register.yearGroup ?? "");
      setTerm(register.term ?? "");
      setSchoolYear(register.schoolYear ?? "");
      setDescription(register.description ?? "");
      setTitleTouched(false);
    }
  }, [open, register.id]);

  function handleClose() {
    setTitleTouched(false);
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTitleTouched(true);
    if (!title.trim()) return;

    updateRegister.mutate(
      {
        title: title.trim(),
        subject: subject.trim() || null,
        yearGroup: yearGroup.trim() || null,
        term: term.trim() || null,
        schoolYear: schoolYear.trim() || null,
        description: description.trim() || null,
      },
      {
        onSuccess: () => {
          toast({ title: "Register updated" });
          onClose();
        },
        onError: (err: unknown) => {
          toast({
            title: "Couldn't update register",
            description: getApiErrorMessage(err, "Please try again."),
            variant: "destructive",
          });
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit program register</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="edit-reg-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-reg-title"
              placeholder="e.g. Year 7 English — Term 2 Program"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setTitleTouched(true)}
              className={
                titleTouched && !title.trim() ? "border-destructive" : ""
              }
              autoFocus
            />
            {titleTouched && !title.trim() && (
              <p className="text-xs text-destructive">Title is required.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-reg-subject">Subject / Program name</Label>
              <Input
                id="edit-reg-subject"
                placeholder="e.g. English"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <p className="text-xs text-gray-400">Free text — doesn't need to match a saved Subject.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-reg-year-group">Year group</Label>
              <Input
                id="edit-reg-year-group"
                placeholder="e.g. Year 7"
                value={yearGroup}
                onChange={(e) => setYearGroup(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-reg-term">Term</Label>
              <Input
                id="edit-reg-term"
                placeholder="e.g. Term 2"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-reg-school-year">School year</Label>
              <Input
                id="edit-reg-school-year"
                placeholder="e.g. 2025"
                value={schoolYear}
                onChange={(e) => setSchoolYear(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-reg-description">Description</Label>
            <Textarea
              id="edit-reg-description"
              placeholder="Optional overview or notes"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateRegister.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateRegister.isPending}>
              {updateRegister.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Register card ─────────────────────────────────────────────────────────────

interface RegisterCardProps {
  register: ProgramRegister;
  onDelete: (id: number) => void;
}

function RegisterCard({ register, onDelete }: RegisterCardProps) {
  const [, setLocation] = useLocation();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const deleteRegister = useDeleteRegister();
  const { toast } = useToast();

  const meta = [register.subject, register.yearGroup, register.term]
    .filter(Boolean)
    .join(" · ");

  function handleArchive(e: React.MouseEvent) {
    e.stopPropagation();
    setConfirmArchive(true);
  }

  function handleEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setEditOpen(true);
  }

  function confirmArchiveAction() {
    deleteRegister.mutate(register.id, {
      onSuccess: () => {
        toast({ title: "Register archived" });
        onDelete(register.id);
      },
      onError: (err: unknown) => {
        toast({
          title: "Couldn't archive register",
          description: getApiErrorMessage(err, "Please try again."),
          variant: "destructive",
        });
      },
    });
    setConfirmArchive(false);
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setLocation(`/app/program-registration/${register.id}`)}
        onKeyDown={(e) =>
          e.key === "Enter" &&
          setLocation(`/app/program-registration/${register.id}`)
        }
        className="group flex items-start gap-3 p-4 rounded-xl border bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mt-0.5">
          <ClipboardList className="h-4.5 w-4.5 text-primary" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-foreground leading-snug truncate">
            {register.title}
          </div>
          {meta && (
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {meta}
            </div>
          )}
          {register.schoolYear && (
            <Badge variant="outline" className="mt-1.5 text-xs px-1.5 py-0">
              {register.schoolYear}
            </Badge>
          )}
          <div className="text-xs text-muted-foreground mt-1.5">
            Updated{" "}
            {format(new Date(register.updatedAt), "d MMM yyyy")}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            title="Edit register"
            onClick={handleEdit}
            className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Archive register"
            onClick={handleArchive}
            className="p-1.5 rounded hover:bg-amber-50 text-muted-foreground hover:text-amber-600 transition-colors"
          >
            <Archive className="h-3.5 w-3.5" />
          </button>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      <EditRegisterDialog
        register={register}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this register?</AlertDialogTitle>
            <AlertDialogDescription>
              "{register.title}" will be moved to Past programs. You can restore it from there at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmArchiveAction}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Dashboard page ────────────────────────────────────────────────────────────

export default function ProgramRegistrationDashboard() {
  const { data: registers = [], isLoading, error } = useProgramRegisters();
  const [createOpen, setCreateOpen] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  const { data: archivedRegisters = [] } = useArchivedProgramRegisters({
    enabled: pastOpen,
  });
  const restoreRegister = useRestoreRegister();
  const { toast } = useToast();

  const errorStatus = error ? (error as { status?: number }).status : undefined;

  // Determine whether to show the "New register" action.
  // Hidden only when the feature is explicitly disabled on this server (404).
  // On transient errors the header still renders so the user can retry via
  // the create dialog — and so E2E tests can always locate the CTA button.
  const showCreateAction = errorStatus !== 404;

  async function handleRestore(reg: ProgramRegister) {
    try {
      await restoreRegister.mutateAsync(reg.id);
      toast({ title: "Register restored" });
    } catch (err) {
      toast({
        title: "Couldn't restore register",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header — always rendered so the "New register" button is always reachable */}
      <div className="border-b px-4 sm:px-6 py-4 flex items-center justify-between bg-white sticky top-0 z-10">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Program Registration</h1>
          {!error && registers.length > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {registers.length} register{registers.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showCreateAction && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPastOpen(true)}
            >
              <History className="mr-1.5 h-4 w-4" />
              Past programs
            </Button>
          )}
          {showCreateAction && (
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="mr-1.5 h-4 w-4" />
              New register
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center p-8">
            <BookOpen className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">
              {errorStatus === 404
                ? "Program Registration is not enabled yet."
                : "Failed to load registers. Please try again."}
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : registers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center max-w-sm mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <ClipboardList className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-1">
              No program registers yet
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Create your first register to start tracking program delivery,
              reflection notes, and support strategies.
            </p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Create first register
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl">
            {registers.map((reg) => (
              <RegisterCard
                key={reg.id}
                register={reg}
                onDelete={() => {}}
              />
            ))}
          </div>
        )}
      </div>

      <CreateRegisterDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {/* Past programs dialog */}
      <Dialog open={pastOpen} onOpenChange={setPastOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Past programs</DialogTitle>
          </DialogHeader>
          {archivedRegisters.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No archived program registers yet. Registers move here when deleted or archived at year-end.
            </p>
          ) : (
            <div
              className="border rounded-lg divide-y max-h-96 overflow-y-auto"
              data-testid="past-programs-list"
            >
              {archivedRegisters.map((reg) => {
                const meta = [reg.subject, reg.yearGroup, reg.term]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div
                    key={reg.id}
                    className="flex items-center justify-between px-3 py-2.5 gap-2"
                    data-testid={`past-program-row-${reg.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{reg.title}</p>
                      {meta && (
                        <p className="text-xs text-muted-foreground truncate">{meta}</p>
                      )}
                      {reg.schoolYear && (
                        <Badge variant="outline" className="mt-0.5 text-xs px-1.5 py-0">
                          {reg.schoolYear}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs gap-1.5 text-blue-600 flex-shrink-0"
                      onClick={() => handleRestore(reg)}
                      disabled={restoreRegister.isPending}
                      data-testid={`button-restore-program-${reg.id}`}
                      data-write-action
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                      Restore
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPastOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
